import { Fragment, useMemo, type FunctionComponent, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { matchPath, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import {
  NavigationBar,
  TOPBAR_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from '@retailer-platform/shared-components/src/tds'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { url as resolveUrlPattern } from '../../../utils/parsing/url'
import icPlatformLogo from '../nav-v2/assets/ic-platform.svg'
import { type NavMenuHierarchyL1, type NavMenuHierarchyL2 } from '../nav-v2/NavMenuHierarchy'
// TODO(nav-v3): temporarily hidden — re-enable once we're ready.
// import { TdsAppSwitcher } from './TdsAppSwitcher'
import { TdsHelpMenu } from './TdsHelpMenu'
import { TdsNotificationsButton } from './TdsNotificationsButton'
import { TdsPartnerMenu } from './TdsPartnerMenu'
import { TdsSearchButton } from './TdsSearchButton'
import { TopBarMenuProvider } from './TopBarMenuContext'
import { NAV_SECTION_TITLES, NavSectionV3, type NavMenuHierarchyV3 } from './navSectionV3'

type Props = {
  navMenuHierarchy: NavMenuHierarchyV3
  isCollapsed: boolean
  onCollapsedChange: (next: boolean) => void
  // Route name to navigate to when the top-left logo is clicked. Resolved via
  // the same useResolveNavHref hook used by NavItems, so active-route params
  // (e.g. :partner_id, :warehouse_id) are substituted from the current URL.
  homeRoute: string
  // Optional override for the TopBar's right cluster. When omitted, the default
  // partner-scoped cluster (Notifications + Search + Help + Partner) renders.
  // Admin sections that lack PartnerContext should pass a partner-safe cluster here.
  topBarRight?: ReactNode
}

const DefaultRetailerTopBarRight: FunctionComponent = () => (
  <>
    <TdsNotificationsButton />
    <TdsSearchButton />
    <TdsHelpMenu />
    <TdsPartnerMenu />
  </>
)

type ResolvedNavHref = { href: string; isExternal: boolean } | undefined

type Navigable = { href?: string; route?: string; newTab?: boolean }

export const useResolveNavHref = (item: Navigable): ResolvedNavHref => {
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()

  return useMemo(() => {
    if (item.href) return { href: item.href, isExternal: true }
    if (!item.route) return undefined

    const pattern = routesByName[item.route]
    if (!pattern) return undefined

    return {
      href: resolveUrlPattern(pattern, (routeMatch?.params as Record<string, string>) ?? {}),
      isExternal: Boolean(item.newTab),
    }
  }, [item.href, item.route, item.newTab, routesByName, routeMatch?.params])
}

// Active when the current URL matches the item's route pattern (prefix match
// — react-router's default for matchPath). External-href items can't be active.
export const useIsNavItemActive = (item: Navigable): boolean => {
  const { routesByName } = useCoreContext()
  const { pathname } = useLocation()

  if (item.href || !item.route) return false
  const pattern = routesByName[item.route]
  if (!pattern) return false

  return Boolean(matchPath(pathname, { path: pattern }))
}

const TdsNavLeaf: FunctionComponent<{ l2: NavMenuHierarchyL2 }> = ({ l2 }) => {
  const resolved = useResolveNavHref(l2)
  const isActive = useIsNavItemActive(l2)
  return (
    <NavigationBar.NavItem
      href={resolved?.href}
      isExternal={resolved?.isExternal ?? false}
      isActive={isActive}
    >
      <FormattedMessage id={l2.name} />
    </NavigationBar.NavItem>
  )
}

// Non-expandable L1: a NavGroup with only a NavGroupTrigger (no NavGroupContent)
// — TDS suppresses the chevron and routes clicks via the trigger's href instead
// of toggling expansion (NavGroup's `hasContent` stays false).
const TdsLeafL1: FunctionComponent<{ l1: NavMenuHierarchyL1 }> = ({ l1 }) => {
  const resolved = useResolveNavHref(l1)
  const isActive = useIsNavItemActive(l1)
  const L1Icon = l1.icon
  return (
    <NavigationBar.NavGroup>
      <NavigationBar.NavGroupTrigger
        href={resolved?.href}
        icon={<L1Icon size={20} />}
        isActive={isActive}
      >
        <FormattedMessage id={l1.name} />
      </NavigationBar.NavGroupTrigger>
    </NavigationBar.NavGroup>
  )
}

export const TdsNavigationBar: FunctionComponent<Props> = ({
  navMenuHierarchy,
  isCollapsed,
  onCollapsedChange,
  homeRoute,
  topBarRight,
}) => {
  const intl = useIntl()
  const homeHref = useResolveNavHref({ route: homeRoute })?.href
  return (
    <NavigationBar>
      <NavigationBar.TopBar>
        <TopBarMenuProvider>
          <NavigationBar.TopBarLeft>
            {/* TODO(nav-v3): temporarily hidden — re-enable once we're ready. */}
            {/* <TdsAppSwitcher /> */}
            {homeHref ? (
              <Link
                to={homeHref}
                aria-label="Instacart Platform home"
                style={{ display: 'inline-flex' }}
              >
                <img
                  src={icPlatformLogo}
                  alt="Instacart Platform"
                  style={{ height: 18, display: 'block' }}
                />
              </Link>
            ) : (
              <img
                src={icPlatformLogo}
                alt="Instacart Platform"
                style={{ height: 18, display: 'block' }}
              />
            )}
          </NavigationBar.TopBarLeft>
          <NavigationBar.TopBarRight>
            {topBarRight ?? <DefaultRetailerTopBarRight />}
          </NavigationBar.TopBarRight>
        </TopBarMenuProvider>
      </NavigationBar.TopBar>
      <NavigationBar.Sidebar isCollapsed={isCollapsed} onCollapsedChange={onCollapsedChange}>
        <NavigationBar.SidebarContent aria-label="Primary navigation">
          {navMenuHierarchy.sections.map(section => (
            <Fragment key={section.id}>
              {section.id !== NavSectionV3.Global && (
                <NavigationBar.NavSection
                  title={intl.formatMessage({ id: NAV_SECTION_TITLES[section.id] })}
                />
              )}
              {section.l1s.map((l1, l1Idx) => {
                if (l1.route || l1.href) {
                  return <TdsLeafL1 key={`${l1.name}-${l1Idx}`} l1={l1} />
                }
                const L1Icon = l1.icon
                return (
                  <NavigationBar.NavGroup key={`${l1.name}-${l1Idx}`}>
                    <NavigationBar.NavGroupTrigger icon={<L1Icon size={20} />}>
                      <FormattedMessage id={l1.name} />
                    </NavigationBar.NavGroupTrigger>
                    <NavigationBar.NavGroupContent>
                      {l1.hasL2Children
                        ? l1.children.map((l2, j) => (
                            <Fragment key={`${l2.name}-${j}`}>
                              <NavigationBar.NavSection
                                title={intl.formatMessage({ id: l2.name })}
                              />
                              {(l2.children ?? []).map((l3, k) => (
                                <TdsNavLeaf key={`${l3.name}-${k}`} l2={l3} />
                              ))}
                            </Fragment>
                          ))
                        : l1.children.map((l2, j) => (
                            <TdsNavLeaf key={`${l2.name}-${j}`} l2={l2} />
                          ))}
                    </NavigationBar.NavGroupContent>
                  </NavigationBar.NavGroup>
                )
              })}
            </Fragment>
          ))}
        </NavigationBar.SidebarContent>
        <NavigationBar.SidebarFooter />
      </NavigationBar.Sidebar>
    </NavigationBar>
  )
}

// Re-exported so consumers building layout around the NavigationBar (e.g. sizing the
// content area to the right of the Sidebar and below the TopBar) can use the canonical
// TDS constants instead of hardcoding them.
export { TOPBAR_HEIGHT, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH }
